import { Armchair, Calendar, Clock, Users } from "lucide-mui"
import { DateTime } from "luxon"
import React from "react"

import i18n from "@/i18n"
import { type ReservationFormValues } from "@/pages/public/venue"
import { formatTableName } from "@/shared/tables"

import { type GuestInformationStepProps } from "./shared"

const STYLES = {
  bookingSummaryCard:
    "rounded-[10px] border border-[#f4f4f4] bg-white px-3.5 py-[14px] shadow-[2px_2px_10px_0px_rgba(0,0,0,0.05)]",
  summaryRows: "space-y-4",
  summaryRow: "flex items-center justify-between gap-4",
  summaryIconGroup: "flex items-center gap-2 text-[#929baa]",
  summaryIcon: "h-3.5 w-3.5",
  summaryLabel: "text-sm leading-5",
  summaryValue: "text-right text-sm leading-5 text-[#171717]",
  timezoneLabel: "ml-1 text-[10px] text-[#cacaca]",
} as const

type BookingSummaryCardProps = {
  values: ReservationFormValues
  timezone?: GuestInformationStepProps["timezone"]
  tables: NonNullable<GuestInformationStepProps["tables"]>
}

const zoned = (dateTime: DateTime, timezone?: string) => (timezone ? dateTime.setZone(timezone) : dateTime)

const getSummary = ({ values, timezone, tables }: BookingSummaryCardProps) => {
  const selectedTable = tables.find((table) => table.id === values.tableId)
  const startTime = values.startTime ? zoned(DateTime.fromISO(values.startTime), timezone) : null
  const endTime = values.endTime ? zoned(DateTime.fromISO(values.endTime), timezone) : null

  return {
    tableName: selectedTable ? formatTableName(selectedTable, { includeGroupName: false }) : null,
    date: values.date ? zoned(values.date, timezone).toFormat("EEEE - MMM dd, yyyy") : null,
    timeRange: startTime && endTime ? `${startTime.toFormat("HH:mm")} - ${endTime.toFormat("HH:mm")}` : null,
    timezoneLabel: startTime?.toFormat("ZZZZ") || null,
  }
}

const BookingSummaryCard: React.FC<BookingSummaryCardProps> = ({ values, timezone, tables }) => {
  const summary = getSummary({ values, timezone, tables })

  return (
    <div className={STYLES.bookingSummaryCard} data-testid="mobile-sheet-booking-summary">
      <div className={STYLES.summaryRows}>
        {!!summary.date && (
          <div className={STYLES.summaryRow} data-testid="mobile-sheet-booking-summary-date">
            <div className={STYLES.summaryIconGroup}>
              <Calendar className={STYLES.summaryIcon} />
              <span className={STYLES.summaryLabel}>{i18n.t("app.public.venue.modal.date")}</span>
            </div>
            <span className={STYLES.summaryValue}>{summary.date}</span>
          </div>
        )}

        <div className={STYLES.summaryRow} data-testid="mobile-sheet-booking-summary-people">
          <div className={STYLES.summaryIconGroup}>
            <Users className={STYLES.summaryIcon} />
            <span className={STYLES.summaryLabel}>{i18n.t("app.public.venue.modal.people_amount")}</span>
          </div>
          <span className={STYLES.summaryValue}>
            {i18n.t("app.public.venue.guests", { count: values.peopleAmount })}
          </span>
        </div>

        {!!summary.timeRange && (
          <div className={STYLES.summaryRow} data-testid="mobile-sheet-booking-summary-time">
            <div className={STYLES.summaryIconGroup}>
              <Clock className={STYLES.summaryIcon} />
              <span className={STYLES.summaryLabel}>{i18n.t("app.public.venue.time")}</span>
            </div>
            <div className={STYLES.summaryValue}>
              <span>{summary.timeRange}</span>
              {!!summary.timezoneLabel && <span className={STYLES.timezoneLabel}>({summary.timezoneLabel})</span>}
            </div>
          </div>
        )}

        {!!summary.tableName && (
          <div className={STYLES.summaryRow} data-testid="mobile-sheet-booking-summary-table">
            <div className={STYLES.summaryIconGroup}>
              <Armchair className={STYLES.summaryIcon} />
              <span className={STYLES.summaryLabel}>{i18n.t("app.public.venue.modal.table")}</span>
            </div>
            <span className={STYLES.summaryValue}>{summary.tableName}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default BookingSummaryCard
